import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchCampaignById, fetchDonationStatusByCampaignId, fetchShipmentsByCampaignId } from "../../services/CampaignService";
import { Campaign } from "../../types/campaign.types";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { FiLayers, FiSettings } from "react-icons/fi";
import NewCampaignCreateModel from "../../components/Campaign/NewCampainCreateModel";
import CampainPosts from "../../components/Campaign/CampainPosts";
import { VscTypeHierarchySub } from "react-icons/vsc";
import Select from "react-select";
import { formatDate } from "../../utils/dateFormatter";
import { Badge } from "../../components/Badge";
import { getReactSelectStyles } from "../../utils/reactSelectStyles";

export default function CampaignDetails() {
    const { id } = useParams();
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [openCampainCreateModal, setOpenCampainCreateModal] = useState(false);
    const [openCampainPostsModal, setOpenCampainPostsModal] = useState(false);
    const [activeTab, setActiveTab] = useState<"live" | "accumulated">("accumulated");

    // Shipment state
    const [shippedBoxes, setShippedBoxes] = useState<any[]>([]);
    const [receivedBoxes, setReceivedBoxes] = useState<any[]>([]);
    const [dispatchedBoxes, setDispatchedBoxes] = useState<any[]>([]);
    const [deliveredBoxes, setDeliveredBoxes] = useState<any[]>([]);
    const [issues, setIssues] = useState<any[]>([]);

    // Donated bars state
    const [donatedBars, setDonatedBars] = useState<number>(0);
    const [donatedBarsHistory, setdonatedBarsHistory] = useState<any[]>([]);

    // Add a state to store shipments data
    const [shipments, setShipments] = useState<any>({});
    const [selectedShipments, setSelectedShipments] = useState<any[]>([]);

    const shipmentOptions = Object.values(shipments)
        .flat()
        .map((item: any) => ({
            value: item.id,
            label: item.name || item.label || "-",
        }))
        .filter((item, idx, arr) => item.value && arr.findIndex(i => i.value === item.value) === idx);

    const filteredShipments = selectedShipments.length === 0
        ? Object.values(shipments).flat()
        : Object.values(shipments)
            .flat()
            .filter((item: any) =>
                selectedShipments.some(sel => sel.value === item.id)
            );

    const getDescriptionParts = (desc: string) => {
        const match = desc.match(/(.*?[.?!])(\s+[\s\S]*)?/);
        if (!match) return [desc, ""];
        return [match[1], match[2] || ""];
    };

    const processShipments = (shipmentsData: any, view: "live" | "accumulated") => {
        const getProcessed = (arr: any[]) =>
            arr.map((item: any) => ({
                time: item.createAt ? formatDate(item.createAt) : "",
                qty: item.orderedBoxUnit,
                scan: item.label,
            }));

        const deliveredArr = shipmentsData?.DELIVERED || [];
        const dispatchedArr = shipmentsData?.CREATED || [];
        const shippedArr = shipmentsData?.SHIPPED || [];
        const missingArr = shipmentsData?.MISSING || [];

        if (view === "accumulated") {
            return {
                shipped: getProcessed([...(shippedArr), ...(deliveredArr)]),
                received: getProcessed(deliveredArr),
                dispatched: getProcessed(dispatchedArr),
                delivered: getProcessed(deliveredArr),
                issues: (missingArr).map((item: any) => ({
                    time: item.createAt ? formatDate(item.createAt) : "",
                    sentBy: item.supplierCode || item.name || "Unknown",
                })),
            };
        } else {
            return {
                shipped: getProcessed(shippedArr),
                received: getProcessed(deliveredArr),
                dispatched: getProcessed(dispatchedArr),
                delivered: getProcessed(deliveredArr),
                issues: (missingArr).map((item: any) => ({
                    time: item.createAt ? formatDate(item.createAt) : "",
                    sentBy: item.supplierCode || item.name || "Unknown",
                })),
            };
        }
    };

    const fetchCampaign = async () => {
        const response = await fetchCampaignById(id);
        if (response?.status === 200) {
            setCampaign(response.data);
        }

        const resDonationStatus = await fetchDonationStatusByCampaignId(id);
        const donationsHistory = resDonationStatus?.data;
        setdonatedBarsHistory(donationsHistory);

        const donationsSum = resDonationStatus?.data?.reduce((sum: number, item: { numberOfUnits?: number }) => sum + (item.numberOfUnits || 0), 0) || 0;
        setDonatedBars(donationsSum);

        const resShipments = await fetchShipmentsByCampaignId(id);
        const shipmentsData = resShipments?.data;
        setShipments(shipmentsData || {});

        const processed = processShipments(shipmentsData, activeTab);

        setShippedBoxes(processed.shipped);
        setReceivedBoxes(processed.received);
        setDispatchedBoxes(processed.dispatched);
        setDeliveredBoxes(processed.delivered);
        setIssues(processed.issues);
    };

    const mapRef = useRef<google.maps.Map | null>(null);

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
    };

    useEffect(() => {
        if (!mapRef.current || filteredShipments.length === 0) return;

        const bounds = new window.google.maps.LatLngBounds();
        // Campaign location
        if (campaign?.lattitude && campaign?.longtitude) {
            bounds.extend({
                lat: Number(campaign.lattitude),
                lng: Number(campaign.longtitude),
            });
        }
        // Shipment markers
        filteredShipments.forEach((item: any) => {
            const lat = Number(item.lattitude || item.latitude);
            const lng = Number(item.longtitude || item.longitude);
            if (lat && lng) {
                bounds.extend({ lat, lng });
            }
        });
        mapRef.current.fitBounds(bounds);
    }, [filteredShipments, campaign?.lattitude, campaign?.longtitude]);

    useEffect(() => {        
        fetchCampaign();
    }, [id, activeTab]);

    if (!campaign) return <div className="text-gray-700 dark:text-gray-300">Loading...</div>;

    // Calculate progress
    const donated = campaign.amountCollected ?? 0;
    const goal = campaign.targetAmount ?? 1;
    const progress = Math.min(100, Math.round((donated / goal) * 100));

    const [firstSentence, restDescription] = getDescriptionParts(campaign.description || "");

    const getShipmentStatusVariant = (status: string): 'success' | 'primary' | 'info' | 'default' => {
        switch (status) {
            case 'DELIVERED': return 'success';
            case 'SHIPPED': return 'primary';
            case 'CREATED': return 'info';
            default: return 'default';
        }
    };

    
    const handleCampainCreateModalClose = () => {
        setOpenCampainCreateModal(false);
        fetchCampaign();
    };   

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="bg-white dark:bg-slate-900/60 rounded-lg shadow dark:shadow-none dark:border dark:border-white/10 flex flex-col md:flex-row items-start p-4 md:p-6 gap-6">
                <img
                    src={campaign.banner}
                    alt={campaign.name}
                    className="w-full md:w-96 object-contain rounded-lg max-h-96"
                />

                <div className="flex-1 flex flex-col gap-2">
                    <div className="flex items-start justify-between w-full">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">{campaign.name}</h1>
                            <p
                                className="text-base font-medium text-gray-800 dark:text-gray-200 mb-1"
                                dangerouslySetInnerHTML={{
                                    __html: showFullDescription
                                        ? campaign.description
                                        : firstSentence
                                }}
                            />
                        </div>
                        <div className="relative">
                            <button 
                                className="ml-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMenu(!showMenu);
                                }}
                            >
                                <FiSettings className="h-7 w-7" />
                            </button>
                            {showMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg z-10">
                                    <div className="py-1">
                                        <button
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                                            onClick={() => {setOpenCampainCreateModal(true); setShowMenu(false);}}
                                        >
                                            Update
                                        </button>
                                        <button
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                                            onClick={() => { setOpenCampainPostsModal(true); setShowMenu(false); }}
                                        >
                                            Post
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-between w-full">
                        <span className="text-gray-500 dark:text-gray-400 text-base">Local partner:</span>
                        {restDescription && !showFullDescription && (
                            <button
                                onClick={() => setShowFullDescription(true)}
                                className="text-green-600 font-medium text-base hover:underline"
                                type="button"
                            >
                                See more
                            </button>
                        )}
                    </div>
                    <div className="flex gap-4 w-full">
                        <div className="flex-1">
                            <div className="bg-gray-200 dark:bg-gray-700 h-2 rounded-full w-full mb-2">
                                <div className="bg-green-400 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                            <div className="grid grid-cols-4 gap-4 mb-4">
                                <div className="col-span-3 text-gray-600 dark:text-gray-400">Total bars donated</div>
                                <div className="text-right text-gray-600 dark:text-gray-400">Goal</div>
                                <div className="col-span-3 font-bold text-gray-900 dark:text-gray-100">{donated}</div>
                                <div className="font-bold text-gray-900 dark:text-gray-100 text-right">{goal.toLocaleString()}</div>
                            </div>
                        </div>
                        <div className="flex-[3] text-right">
                            <Badge
                                label={campaign.isActive ? 'Active' : 'Deactivated'}
                                variant={campaign.isActive ? 'success' : 'default'}
                                size="lg"
                            />
                            <div className="text-gray-400 dark:text-gray-500 text-base">
                                Created: <span className="font-semibold text-gray-600 dark:text-gray-300">{formatDate(String(campaign.createdAt))}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-100">Campaign shipments</h2>
            <div className="flex flex-col gap-4">
                <div className="mb-4 flex items-center gap-4">
                    <div className="min-w-[250px]">
                        <Select
                            isMulti
                            options={shipmentOptions}
                            value={selectedShipments}
                            onChange={(newValue) => setSelectedShipments([...newValue])}
                            placeholder="Filter by shipment"
                            classNamePrefix="react-select"
                            styles={getReactSelectStyles()}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-white dark:bg-slate-900/60 rounded-lg p-6">
                    <div className="flex flex-col gap-6">
                        <div className="h-[400px] min-h-[300px] bg-green-50 dark:bg-green-900/20 rounded-lg p-6 flex flex-col">
                            <div className="flex justify-between mb-2">
                                <span className="font-semibold text-lg text-gray-900 dark:text-gray-100">SHIPMENTS</span>
                                <span className="font-semibold text-lg text-gray-900 dark:text-gray-100">STATUS</span>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                {filteredShipments.map((item: any, idx: number) => (
                                    <div
                                        key={item.id || idx}
                                        className="flex justify-between items-center bg-white dark:bg-slate-800 rounded border border-green-100 dark:border-green-800 px-3 py-2 mb-2 text-sm"
                                    >
                                        <div className="flex-1 font-medium text-gray-800 dark:text-gray-200">{item.name || item.label || "-"}</div>
                                        <div className="flex-1 flex justify-end">
                                            <Badge
                                                label={item.currentStutus || "-"}
                                                variant={getShipmentStatusVariant(item.currentStutus)}
                                                size="md"
                                                className="min-w-[80px]"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-green-50 dark:bg-green-900/20 rounded p-4 border border-gray-100 dark:border-white/10">
                                <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-semibold">TOTAL BARS</div>
                                <div className="text-3xl font-bold text-gray-900 dark:text-white">{donatedBars} {/*campaign.targetAmount*/}</div>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 rounded p-4 border border-gray-100 dark:border-white/10">
                                <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-semibold">TOTAL BOXES</div>
                                <div className="text-3xl font-bold text-gray-900 dark:text-white">{(Math.round(donatedBars/36)) /* totalBoxes */}</div>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 rounded p-4 border border-gray-100 dark:border-white/10">
                                <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-semibold">GOAL BARS</div>
                                <div className="text-3xl font-bold text-gray-900 dark:text-white">{goal.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg overflow-hidden h-[520px] min-h-[300px] bg-white dark:bg-slate-800">
                        <GoogleMap
                            mapContainerStyle={{
                                width: '100%',
                                height: '100%'
                            }}
                            center={{
                                lat: Number(campaign.lattitude) || 0,
                                lng: Number(campaign.longtitude) || 0
                            }}
                            zoom={10}
                            onLoad={handleMapLoad}
                        >
                            {/* <Marker
                                position={{
                                    lat: Number(campaign.lattitude) || 0,
                                    lng: Number(campaign.longtitude) || 0
                                }}
                                // A green marker icon is used for show campaign
                                icon={{
                                    url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png"
                                }}
                            /> */}
                            {filteredShipments.map((item: any, idx: number) => {
                                const lat = Number(item.lattitude || item.latitude);
                                const lng = Number(item.longtitude || item.longitude);
                                if (!lat || !lng) return null;
                                return (
                                    <Marker
                                        key={item.id || idx}
                                        position={{ lat, lng }}
                                        label={item.name || item.label || undefined}
                                    />
                                );
                            })}
                        </GoogleMap>
                    </div>
                </div>
            </div>
            <div className="mt-12">
                <div className="flex items-center gap-x-8 mb-2">
                    <button
                        className={`flex items-center gap-2 pb-1 font-semibold border-b-2 ${
                            activeTab === "live"
                                ? "border-black dark:border-white text-black dark:text-white"
                                : "border-transparent text-gray-400 dark:text-gray-500"
                        }`}
                        onClick={() => setActiveTab("live")}
                    >
                        <VscTypeHierarchySub className="w-5 h-5" />
                        Live view
                    </button>
                    <button
                        className={`flex items-center gap-2 pb-1 font-semibold border-b-2 ${
                            activeTab === "accumulated"
                                ? "border-black dark:border-white text-black dark:text-white"
                                : "border-transparent text-gray-400 dark:text-gray-500"
                        }`}
                        onClick={() => setActiveTab("accumulated")}
                    >
                        <FiLayers className="w-5 h-5" />
                        Accumulated view
                    </button>
                </div>
                <div className="grid grid-cols-6 gap-4">
                    <div className="flex flex-col">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-t p-4 border-b-2 border-green-500 dark:border-green-400">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">Donated (Bars)</div>
                            <div className="text-3xl font-bold mt-2 text-gray-900 dark:text-white">
                                {donatedBars}
                            </div>
                        </div>
                        <div className="h-4"></div>
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-green-200 dark:border-green-700 text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
                            <div className="flex-1">Time</div>
                            <div className="flex-1">Donation</div>
                        </div>
                        <div className="h-2"></div>
                        <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded-b p-4 min-h-[180px] max-h-[300px] overflow-y-auto flex flex-col gap-2">
                            {donatedBarsHistory.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex bg-white dark:bg-slate-800 rounded shadow-sm border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 items-center"
                                >
                                    <div className="flex-1 whitespace-pre-line">
                                        {item.donationDate ? formatDate(item.donationDate).replace(" ", "\n") : ""}
                                    </div>
                                    <div className="flex-1">{item.numberOfUnits} bars</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-t p-4 border-b-2 border-green-500 dark:border-green-400">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">Shipped (Boxes)</div>
                            <div className="text-3xl font-bold mt-2 text-gray-900 dark:text-white">{shippedBoxes.reduce((sum, d) => sum + d.qty, 0)}</div>
                        </div>
                        <div className="h-4"></div>
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-green-200 dark:border-green-700 text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
                            <div className="flex-1">Time</div>
                            <div className="flex-1">Qty</div>
                            <div className="flex-1">Scan</div>
                        </div>
                        <div className="h-2"></div>
                        <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded-b p-4 min-h-[180px] max-h-[300px] overflow-y-auto flex flex-col gap-2">
                            {shippedBoxes.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex bg-white dark:bg-slate-800 rounded shadow-sm border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 items-center"
                                >
                                    <div className="flex-1 whitespace-pre-line">{item.time.replace(" ", "\n")}</div>
                                    <div className="flex-1">{item.qty}</div>
                                    <div className="flex-1">{item.scan}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-t p-4 border-b-2 border-green-500 dark:border-green-400">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">Received (Boxes)</div>
                            <div className="text-3xl font-bold mt-2 text-gray-900 dark:text-white">{receivedBoxes.reduce((sum, d) => sum + d.qty, 0)}</div>
                        </div>
                        <div className="h-4"></div>
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-green-200 dark:border-green-700 text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
                            <div className="flex-1">Time</div>
                            <div className="flex-1">Qty</div>
                            <div className="flex-1">Scan</div>
                        </div>
                        <div className="h-2"></div>
                        <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded-b p-4 min-h-[180px] max-h-[300px] overflow-y-auto flex flex-col gap-2">
                            {receivedBoxes.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex bg-white dark:bg-slate-800 rounded shadow-sm border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 items-center"
                                >
                                    <div className="flex-1 whitespace-pre-line">{item.time.replace(" ", "\n")}</div>
                                    <div className="flex-1">{item.qty}</div>
                                    <div className="flex-1">{item.scan}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-t p-4 border-b-2 border-green-500 dark:border-green-400">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">Dispatched (Boxes)</div>
                            <div className="text-3xl font-bold mt-2 text-gray-900 dark:text-white">{dispatchedBoxes.reduce((sum, d) => sum + d.qty, 0)}</div>
                        </div>
                        <div className="h-4"></div>
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-green-200 dark:border-green-700 text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
                            <div className="flex-1">Time</div>
                            <div className="flex-1">Qty</div>
                            <div className="flex-1">Scan</div>
                        </div>
                        <div className="h-2"></div>
                        <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded-b p-4 min-h-[180px] max-h-[300px] overflow-y-auto flex flex-col gap-2">
                            {dispatchedBoxes.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex bg-white dark:bg-slate-800 rounded shadow-sm border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 items-center"
                                >
                                    <div className="flex-1 whitespace-pre-line">{item.time.replace(" ", "\n")}</div>
                                    <div className="flex-1">{item.qty}</div>
                                    <div className="flex-1">{item.scan}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-t p-4 border-b-2 border-green-500 dark:border-green-400">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">Delivered (Boxes)</div>
                            <div className="text-3xl font-bold mt-2 text-gray-900 dark:text-white">{deliveredBoxes.reduce((sum, d) => sum + d.qty, 0)}</div>
                        </div>
                        <div className="h-4"></div>
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-green-200 dark:border-green-700 text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
                            <div className="flex-1">Time</div>
                            <div className="flex-1">Qty</div>
                            <div className="flex-1">Scan</div>
                        </div>
                        <div className="h-2"></div>
                        <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded-b p-4 min-h-[180px] max-h-[300px] overflow-y-auto flex flex-col gap-2">
                            {deliveredBoxes.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex bg-white dark:bg-slate-800 rounded shadow-sm border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 items-center"
                                >
                                    <div className="flex-1 whitespace-pre-line">{item.time.replace(" ", "\n")}</div>
                                    <div className="flex-1">{item.qty}</div>
                                    <div className="flex-1">{item.scan}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-t p-4 border-b-2 border-green-500 dark:border-green-400">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">Issues</div>
                            <div className="text-3xl font-bold mt-2 text-gray-900 dark:text-white">{issues.length}</div>
                        </div>
                        <div className="h-4"></div>
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-green-200 dark:border-green-700 text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
                            <div className="flex-1">Time</div>
                            <div className="flex-1">Sent by (Team)</div>
                        </div>
                        <div className="h-2"></div>
                        <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded-b p-4 min-h-[180px] max-h-[300px] overflow-y-auto flex flex-col gap-2">
                            {issues.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="flex bg-white dark:bg-slate-800 rounded shadow-sm border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 items-center"
                                >
                                    <div className="flex-1 whitespace-pre-line">{item.time.replace(" ", "\n")}</div>
                                    <div className="flex-1">{item.sentBy}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <NewCampaignCreateModel open={openCampainCreateModal} onClose={handleCampainCreateModalClose} campaignId={id} />
            <CampainPosts
                open={openCampainPostsModal}
                onClose={() => setOpenCampainPostsModal(false)}
                campainId={id as string}
            />
        </div>
    );
}